import { useEffect, useState } from 'react';
import { staffApi, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { PlusCircle, Eye, EyeOff } from 'lucide-react';

const emptyForm = { name: '', description: '', price: '', tiffinCount: '', validityDays: 60, displayOrder: 0 };

export default function PlansTab() {
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [showHidden, setShowHidden] = useState(false);

  const load = () => staffApi.get('/plans').then(({ data }) => setPlans(data.plans));

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (plan) => {
    setEditing(plan);
    setForm({
      name: plan.name,
      description: plan.description || '',
      price: plan.price,
      tiffinCount: plan.tiffinCount,
      validityDays: plan.validityDays,
      displayOrder: plan.displayOrder,
    });
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      if (editing) await staffApi.patch(`/plans/${editing.id}`, form);
      else await staffApi.post('/plans', form);
      toast.success('Plan saved');
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (plan) => {
    await staffApi.patch(`/plans/${plan.id}`, { isActive: !plan.isActive });
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          data-testid="plans-show-hidden-toggle"
          onClick={() => setShowHidden((v) => !v)}
          className="text-sm text-muted-ink hover:text-charcoal flex items-center gap-1.5 transition-colors duration-150"
        >
          {showHidden ? <EyeOff size={14} /> : <Eye size={14} />}
          {showHidden ? 'Hide' : 'Show'} Hidden ({plans.filter((p) => !p.isActive).length})
        </button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="plan-create-btn" onClick={openCreate} className="bg-saffron hover:bg-saffron-hover">
              <PlusCircle size={16} className="mr-2" /> New Plan
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="plan-form-dialog">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Plan' : 'Create Plan'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input data-testid="plan-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Description</Label>
                <Textarea data-testid="plan-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Price (CAD)</Label>
                  <Input type="number" data-testid="plan-price-input" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Tiffins Included</Label>
                  <Input type="number" data-testid="plan-tiffincount-input" value={form.tiffinCount} onChange={(e) => setForm({ ...form, tiffinCount: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Validity (days)</Label>
                  <Input type="number" data-testid="plan-validity-input" value={form.validityDays} onChange={(e) => setForm({ ...form, validityDays: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Display Order</Label>
                  <Input type="number" data-testid="plan-displayorder-input" value={form.displayOrder} onChange={(e) => setForm({ ...form, displayOrder: e.target.value })} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="plan-save-btn" disabled={busy} onClick={save} className="bg-saffron hover:bg-saffron-hover">
                {busy ? 'Saving…' : 'Save Plan'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-xl border border-border/40 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-bone/60">
              <TableHead>Order</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Price (CAD)</TableHead>
              <TableHead>Tiffins</TableHead>
              <TableHead>Validity</TableHead>
              <TableHead>Active</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {plans.filter((p) => showHidden || p.isActive).map((p) => (
              <TableRow key={p.id} data-testid="plan-row">
                <TableCell className="text-muted-ink">{p.displayOrder}</TableCell>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell className="max-w-[220px] text-muted-ink text-sm truncate" title={p.description || ''}>
                  {p.description || <span className="italic opacity-60">No description</span>}
                </TableCell>
                <TableCell>${Number(p.price).toFixed(2)}</TableCell>
                <TableCell>{p.tiffinCount}</TableCell>
                <TableCell>{p.validityDays} days</TableCell>
                <TableCell>
                  <Switch data-testid={`plan-active-toggle-${p.id}`} checked={p.isActive} onCheckedChange={() => toggleActive(p)} />
                </TableCell>
                <TableCell>
                  <button data-testid={`plan-edit-btn-${p.id}`} onClick={() => openEdit(p)} className="text-sm text-coriander hover:underline">
                    Edit
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
